import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    CreditCard, RefreshCw, ChevronLeft, ChevronRight,
    Users, Package, MapPin,
    X, Calendar, Clock, FileText, Eye, CheckCircle, Loader2, Wallet
} from 'lucide-react';
import DataGrid from '../../components/datagrid/DataGrid';
import Notice from '../../components/common/Notice';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import { fleetToast } from '../../utils/toast';
import TlIcon from '../../components/TlIcon';
import { getPaymentStatus, canApprovePayment, canMarkPaymentPaid } from '../../utils/paymentStatus';
import { getISOWeek, getISOWeeksInYear, formatWeekRange, buildWeekPeriodFilter } from '../../utils/payoutWeek';
import './CourierPaymentsPage.css';

/**
 * @param embedded Hakedişler ekranında sekme olarak açıldığında kendi başlığını çizmez.
 * @param onWeekContext Seçili hafta + o haftanın hakediş durumunu üst ekrana bildirir (akış bandı).
 */
const CourierPaymentsPage = ({ embedded = false, onWeekContext }) => {
    const now = getISOWeek(new Date());
    const [selectedYear, setSelectedYear] = useState(now.year);
    const [selectedWeek, setSelectedWeek] = useState(now.week);

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState('TotalEarnings');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filters, setFilters] = useState({});
    const [filterTypes, setFilterTypes] = useState({});

    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showPayForm, setShowPayForm] = useState(false);
    const [payReference, setPayReference] = useState('');
    const [payNote, setPayNote] = useState('');

    const formatCurrency = (amount) => {
        if (amount == null) return '0 TL';
        return formatTL(amount);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDateShort = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('tr-TR');
    };

    const navigateWeek = (dir) => {
        let w = selectedWeek + dir;
        let y = selectedYear;
        if (w < 1) {
            y--;
            w = getISOWeeksInYear(y);
        } else if (w > getISOWeeksInYear(y)) {
            y++;
            w = 1;
        }
        setSelectedYear(y);
        setSelectedWeek(w);
        setCurrentPage(1);
    };

    const goToCurrentWeek = () => {
        const c = getISOWeek(new Date());
        setSelectedYear(c.year);
        setSelectedWeek(c.week);
        setCurrentPage(1);
    };

    const isCurrentWeek = selectedYear === now.year && selectedWeek === now.week;

    const buildFilterModel = useCallback(() => {
        /* Hafta seçimi dönemin BİTİŞ TARİHİNE bakar, hafta numarasına DEĞİL: eski model
           `Week` alanını gönderiyordu, entity'de karşılığı `WeekNumber` olduğu için DynamicGrid
           alanı çözemiyor ve filtreyi SESSİZCE atıyordu. Kural ve hesap filo hakedişi ekranıyla
           ORTAK (utils/payoutWeek). */
        const fm = buildWeekPeriodFilter(selectedYear, selectedWeek);
        Object.keys(filters).forEach((key) => {
            if (filters[key] != null && String(filters[key]).trim() !== '') {
                fm[key] = { filter: filters[key], type: filterTypes[key] || 'contains', caseSensitive: false };
            }
        });
        return fm;
    }, [selectedYear, selectedWeek, filters, filterTypes]);

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiService.post(API_ENDPOINTS.COURIER_PAYMENT.LIST, {
                page: currentPage,
                pageSize,
                sortModels: sortField ? [{ colId: sortField, sort: sortOrder }] : null,
                filterModel: buildFilterModel()
            });
            if (res.success && res.data) {
                const data = res.data.data || res.data;
                setPayments(data.items || []);
                setTotalRecords(data.totalCount || 0);
            } else {
                setError(res.error || 'Veriler yüklenemedi');
            }
        } catch {
            setError('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortField, sortOrder, buildFilterModel]);

    const handleViewDetail = useCallback(async (paymentId) => {
        setDetailLoading(true);
        setShowDetail(true);
        setSelectedPaymentId(paymentId);
        setShowPayForm(false);
        setPayReference('');
        setPayNote('');
        try {
            const res = await apiService.get(API_ENDPOINTS.COURIER_PAYMENT.DETAIL(paymentId));
            if (res.success && res.data) {
                setDetailData(res.data.data || res.data);
            } else {
                setDetailData(null);
            }
        } catch {
            setDetailData(null);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // Akışa uygun yönetim: Onay Bekliyor → Onayla → Onaylandı → Ödendi İşaretle.
    const handleApprove = async () => {
        if (!selectedPaymentId) return;
        setActionLoading(true);
        try {
            const res = await apiService.post(API_ENDPOINTS.COURIER_PAYMENTS.APPROVE(selectedPaymentId));
            if (res.success) {
                fleetToast.success('Hakediş onaylandı');
                await handleViewDetail(selectedPaymentId);
                fetchPayments();
            } else {
                fleetToast.error(res.error || res.message || 'Onay başarısız');
            }
        } catch {
            fleetToast.error('Onay sırasında hata oluştu');
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkPaid = async () => {
        if (!selectedPaymentId) return;
        if (!payReference.trim()) {
            fleetToast.warn('Ödeme referansı zorunlu');
            return;
        }
        setActionLoading(true);
        try {
            const res = await apiService.post(API_ENDPOINTS.COURIER_PAYMENTS.MARK_PAID(selectedPaymentId), {
                paymentReference: payReference.trim(),
                adminNote: payNote.trim() || null,
            });
            if (res.success) {
                fleetToast.success('Ödeme kaydedildi');
                setShowPayForm(false);
                setPayReference('');
                setPayNote('');
                await handleViewDetail(selectedPaymentId);
                fetchPayments();
            } else {
                fleetToast.error(res.error || res.message || 'Ödeme kaydedilemedi');
            }
        } catch {
            fleetToast.error('Ödeme sırasında hata oluştu');
        } finally {
            setActionLoading(false);
        }
    };

    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    /* Akış bandına seçili haftanın durumunu bildir. Haftada birden çok kurye kaydı olabilir;
       EN GERİ adım bildirilir — biri hâlâ fatura bekliyorsa dönem "ödendi" sayılmaz. */
    useEffect(() => {
        if (!onWeekContext) return;
        const statuses = payments.map(p => p.status).filter(s => s != null);
        onWeekContext({
            year: selectedYear,
            week: selectedWeek,
            status: statuses.length ? Math.min(...statuses) : null,
        });
    }, [onWeekContext, selectedYear, selectedWeek, payments]);

    const handleSort = (field, order) => { setSortField(field); setSortOrder(order); };
    const handleFilterChange = (field, value, type) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setFilterTypes(prev => ({ ...prev, [field]: type }));
        setCurrentPage(1);
    };
    const handlePageSizeChange = (size) => { setPageSize(size); setCurrentPage(1); };

    const stats = useMemo(() => {
        const couriers = new Set(payments.map(p => p.courierId)).size;
        const deliveries = payments.reduce((s, p) => s + (p.totalDeliveries || 0), 0);
        const earnings = payments.reduce((s, p) => s + (p.totalEarnings || 0), 0);
        const distance = payments.reduce((s, p) => s + (p.totalDistanceKm || 0), 0);
        return { couriers, deliveries, earnings, distance };
    }, [payments]);

    const statCards = [
        { title: 'Kurye', value: stats.couriers, icon: Users, color: '#6366f1' },
        { title: 'Teslimat', value: stats.deliveries, icon: Package, color: '#22c55e' },
        { title: 'Toplam Kazanç', value: formatCurrency(stats.earnings), icon: TlIcon, color: '#f59e0b' },
        { title: 'Toplam Mesafe', value: `${stats.distance.toFixed(1)} km`, icon: MapPin, color: '#3b82f6' },
    ];

    const columns = useMemo(() => [
        {
            field: 'courierName', header: 'Kurye', sortable: true, filterable: true,
            render: (row) => (
                <div className="cp-courier-cell">
                    <span className="cp-courier-name">{row.courierName}</span>
                    {row.courierPhone && <span className="cp-courier-phone">{row.courierPhone}</span>}
                </div>
            )
        },
        {
            field: 'period', header: 'Dönem', sortable: true,
            render: (row) => (
                <div className="cp-period-cell">
                    <span className="cp-period-text">{row.period || '-'}</span>
                    {row.periodRange && <span className="cp-period-range">{row.periodRange}</span>}
                </div>
            )
        },
        { field: 'totalDeliveries', header: 'Teslimat', sortable: true, render: (row) => <span className="cp-delivery-badge">{row.totalDeliveries || 0}</span> },
        { field: 'totalDistanceKm', header: 'Mesafe', sortable: true, render: (row) => `${(row.totalDistanceKm || 0).toFixed(1)} km` },
        { field: 'totalEarnings', header: 'Kazanç', sortable: true, render: (row) => <span className="cp-amount-highlight">{formatCurrency(row.totalEarnings)}</span> },
        {
            field: 'status', header: 'Durum', sortable: true,
            render: (row) => {
                const st = getPaymentStatus(row.status);
                return <span className={`cp-status-badge ${st.tone}`}>{st.label}</span>;
            }
        },
        {
            field: 'actions', header: '', sortable: false,
            render: (row) => (
                <button className="cp-detail-btn" onClick={(e) => { e.stopPropagation(); handleViewDetail(row.id); }}>
                    <Eye size={14} />
                </button>
            )
        }
    ], [handleViewDetail]);

    const renderCard = (item) => {
        const st = getPaymentStatus(item.status);
        return (
            <div className="cp-payment-card" onClick={() => handleViewDetail(item.id)}>
                <div className="cp-card-header">
                    <div>
                        <h3>{item.courierName}</h3>
                        <p>{item.period} {item.periodRange && `· ${item.periodRange}`}</p>
                    </div>
                    <span className={`cp-status-badge ${st.tone}`}>{st.label}</span>
                </div>
                <div className="cp-card-stats">
                    <div className="cp-card-stat">
                        <span className="cp-card-stat-label">Teslimat</span>
                        <span className="cp-card-stat-value">{item.totalDeliveries || 0}</span>
                    </div>
                    <div className="cp-card-stat">
                        <span className="cp-card-stat-label">Mesafe</span>
                        <span className="cp-card-stat-value">{(item.totalDistanceKm || 0).toFixed(1)} km</span>
                    </div>
                    <div className="cp-card-stat highlight">
                        <span className="cp-card-stat-label">Kazanç</span>
                        <span className="cp-card-stat-value">{formatCurrency(item.totalEarnings)}</span>
                    </div>
                </div>
            </div>
        );
    };

    const closeDetail = () => {
        setShowDetail(false);
        setDetailData(null);
        setSelectedPaymentId(null);
        setShowPayForm(false);
        setPayReference('');
        setPayNote('');
    };

    const renderDetail = () => {
        if (!showDetail) return null;
        const d = detailData;
        return (
            <div className="cp-detail-overlay" onClick={closeDetail}>
                <div className="cp-detail-panel" onClick={e => e.stopPropagation()}>
                    <div className="cp-detail-header">
                        <h2>Hakediş Detayı</h2>
                        <button className="cp-detail-close" onClick={closeDetail}><X size={18} /></button>
                    </div>
                    {detailLoading ? (
                        <div className="cp-detail-loading">
                            <div className="cp-spinner" />
                            <span>Yükleniyor...</span>
                        </div>
                    ) : !d ? (
                        <div className="cp-detail-empty">Detay bilgisi bulunamadı</div>
                    ) : (
                        <div className="cp-detail-body">
                            <div className="cp-detail-section">
                                <div className="cp-detail-courier-info">
                                    <span className="cp-detail-courier-name">{d.courierName}</span>
                                    {d.courierPhone && <span className="cp-detail-courier-phone">{d.courierPhone}</span>}
                                </div>
                                <div className={`cp-status-badge ${getPaymentStatus(d.status).tone}`}>
                                    {getPaymentStatus(d.status).label}
                                </div>
                            </div>

                            {d.period && (
                                <div className="cp-detail-section">
                                    <h3><Calendar size={14} /> Dönem</h3>
                                    <div className="cp-detail-period">
                                        <span>{d.period.display}</span>
                                        {d.period.start && d.period.end && (
                                            <span className="cp-detail-period-range">
                                                {formatDateShort(d.period.start)} - {formatDateShort(d.period.end)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {d.summary && (
                                <div className="cp-detail-section">
                                    <h3><TlIcon size={14} /> Kazanç Özeti</h3>
                                    <div className="cp-detail-grid">
                                        <div className="cp-detail-item">
                                            <span>Teslimat</span>
                                            <strong>{d.summary.totalDeliveries}</strong>
                                        </div>
                                        <div className="cp-detail-item">
                                            <span>Mesafe</span>
                                            <strong>{d.summary.totalDistanceKm?.toFixed(1)} km</strong>
                                        </div>
                                        <div className="cp-detail-item highlight">
                                            <span>Toplam Kazanç</span>
                                            <strong>{formatCurrency(d.summary.totalEarnings)}</strong>
                                        </div>
                                        <div className="cp-detail-item">
                                            <span>Baz Ücret Toplamı</span>
                                            <strong>{formatCurrency(d.summary.baseFeeTotal)}</strong>
                                        </div>
                                        <div className="cp-detail-item">
                                            <span>Mesafe Ücreti Toplamı</span>
                                            <strong>{formatCurrency(d.summary.distanceFeeTotal)}</strong>
                                        </div>
                                        <div className="cp-detail-item">
                                            <span>Ort. Mesafe</span>
                                            <strong>{d.summary.averageDistancePerDelivery?.toFixed(1)} km</strong>
                                        </div>
                                        <div className="cp-detail-item">
                                            <span>Ort. Kazanç</span>
                                            <strong>{formatCurrency(d.summary.averageEarningPerDelivery)}</strong>
                                        </div>
                                        {(() => {
                                            const bonus = d.packageBonus ?? d.summary?.packageBonus ?? 0;
                                            const totalPayable = d.totalPayable ?? d.summary?.totalPayable ?? d.summary.totalEarnings;
                                            const timeBasedEarningTotal = d.timeBasedEarningTotal ?? d.summary?.timeBasedEarningTotal ?? 0;
                                            const workedDayCount = d.workedDayCount ?? d.summary?.workedDayCount ?? 0;
                                            const totalCountedHours = d.totalCountedHours ?? d.summary?.totalCountedHours ?? 0;
                                            return (
                                                <>
                                                    {Number(bonus) > 0 && (
                                                        <div className="cp-detail-item">
                                                            <span>Paket Bonusu</span>
                                                            <strong>{formatCurrency(bonus)}</strong>
                                                        </div>
                                                    )}
                                                    {Number(timeBasedEarningTotal) > 0 && (
                                                        <>
                                                            <div className="cp-detail-item">
                                                                <span>Mesai Hakedişi</span>
                                                                <strong>{formatCurrency(timeBasedEarningTotal)}</strong>
                                                            </div>
                                                            <div className="cp-detail-item">
                                                                <span>Çalışılan Gün</span>
                                                                <strong>{workedDayCount}</strong>
                                                            </div>
                                                            <div className="cp-detail-item">
                                                                <span>Toplam Saat</span>
                                                                <strong>{Number(totalCountedHours).toFixed(2)} sa</strong>
                                                            </div>
                                                        </>
                                                    )}
                                                    <div className="cp-detail-item highlight">
                                                        <span>Toplam</span>
                                                        <strong>{formatCurrency(totalPayable)}</strong>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {d.invoices && d.invoices.length > 0 && (
                                <div className="cp-detail-section">
                                    <h3><FileText size={14} /> Faturalar</h3>
                                    <div className="cp-invoice-list">
                                        {d.invoices.map(inv => {
                                            const ist = getPaymentStatus(inv.status);
                                            return (
                                                <div key={inv.id} className="cp-invoice-item">
                                                    <div className="cp-invoice-info">
                                                        <span className="cp-invoice-number">{inv.invoiceNumber}</span>
                                                        <span className="cp-invoice-date">{formatDateShort(inv.invoiceDate)}</span>
                                                    </div>
                                                    <span className="cp-invoice-amount">{formatCurrency(inv.invoiceAmount)}</span>
                                                    <span className={`cp-status-badge small ${ist.tone}`}>{ist.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="cp-detail-section">
                                <h3><Clock size={14} /> Tarihler</h3>
                                <div className="cp-detail-timeline">
                                    {d.createdAtUtc && (
                                        <div className="cp-timeline-item">
                                            <span className="cp-timeline-dot created" />
                                            <div>
                                                <span className="cp-timeline-label">Oluşturulma</span>
                                                <span className="cp-timeline-date">{formatDate(d.createdAtUtc)}</span>
                                            </div>
                                        </div>
                                    )}
                                    {d.approvedAt && (
                                        <div className="cp-timeline-item">
                                            <span className="cp-timeline-dot approved" />
                                            <div>
                                                <span className="cp-timeline-label">Onaylanma</span>
                                                <span className="cp-timeline-date">{formatDate(d.approvedAt)}</span>
                                            </div>
                                        </div>
                                    )}
                                    {d.paidAt && (
                                        <div className="cp-timeline-item">
                                            <span className="cp-timeline-dot paid" />
                                            <div>
                                                <span className="cp-timeline-label">Ödeme</span>
                                                <span className="cp-timeline-date">{formatDate(d.paidAt)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {d.paymentReference && (
                                    <div className="cp-detail-ref">Referans: {d.paymentReference}</div>
                                )}
                                {d.adminNote && (
                                    <div className="cp-detail-note">Not: {d.adminNote}</div>
                                )}
                            </div>

                            {(canApprovePayment(d.status) || canMarkPaymentPaid(d.status)) && (
                                <div className="cp-detail-actions">
                                    {canApprovePayment(d.status) && (
                                        <button className="cp-action-btn approve" onClick={handleApprove} disabled={actionLoading}>
                                            {actionLoading ? <Loader2 size={16} className="spinning" /> : <CheckCircle size={16} />}
                                            Onayla
                                        </button>
                                    )}
                                    {canMarkPaymentPaid(d.status) && !showPayForm && (
                                        <button className="cp-action-btn pay" onClick={() => setShowPayForm(true)} disabled={actionLoading}>
                                            <Wallet size={16} /> Ödendi İşaretle
                                        </button>
                                    )}
                                    {canMarkPaymentPaid(d.status) && showPayForm && (
                                        <div className="cp-pay-form">
                                            <label className="cp-pay-label">Ödeme Referansı *</label>
                                            <input
                                                className="cp-pay-input"
                                                value={payReference}
                                                onChange={(e) => setPayReference(e.target.value)}
                                                placeholder="Havale/EFT referansı, dekont no…"
                                            />
                                            <label className="cp-pay-label">Not (opsiyonel)</label>
                                            <textarea
                                                className="cp-pay-textarea"
                                                value={payNote}
                                                onChange={(e) => setPayNote(e.target.value)}
                                                rows={2}
                                                placeholder="Açıklama…"
                                            />
                                            <div className="cp-pay-actions">
                                                <button className="cp-action-btn ghost" onClick={() => setShowPayForm(false)} disabled={actionLoading}>
                                                    Vazgeç
                                                </button>
                                                <button className="cp-action-btn pay" onClick={handleMarkPaid} disabled={actionLoading}>
                                                    {actionLoading ? <Loader2 size={16} className="spinning" /> : <Wallet size={16} />}
                                                    Ödemeyi Kaydet
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="cp-container">
            {!embedded && (
                <div className="page-header">
                    <div className="header-left">
                        <div className="page-icon"><CreditCard size={20} /></div>
                        <div className="page-title">
                            <h1>Kurye Hakedişleri</h1>
                            <p>Haftalık kurye hakediş takibi</p>
                        </div>
                    </div>
                    <button className="btn btn-secondary" onClick={fetchPayments}>
                        <RefreshCw size={16} />
                        Yenile
                    </button>
                </div>
            )}

            <div className="cp-week-nav">
                <button className="cp-week-btn" onClick={() => navigateWeek(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className="cp-week-display">
                    <span className="cp-week-label">Hafta {selectedWeek}, {selectedYear}</span>
                    <span className="cp-week-range">{formatWeekRange(selectedYear, selectedWeek)}</span>
                </div>
                <button className="cp-week-btn" onClick={() => navigateWeek(1)}>
                    <ChevronRight size={20} />
                </button>
                {!isCurrentWeek && (
                    <button className="cp-today-btn" onClick={goToCurrentWeek}>
                        Bu Hafta
                    </button>
                )}
            </div>

            {error && (
                <Notice tone="error" title="Hakedişler yüklenemedi">
                    {error} Bağlantınızı kontrol edip Yenile'ye basın.
                </Notice>
            )}

            <div className="cp-stats-grid">
                {statCards.map((card, i) => {
                    const Icon = card.icon;
                    return (
                        <div key={i} className="cp-stat-card">
                            <div className="cp-stat-icon">
                                <Icon size={20} />
                            </div>
                            <div className="cp-stat-content">
                                <span className="cp-stat-label">{card.title}</span>
                                <span className="cp-stat-value">{card.value}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="cp-grid-section">
                <div className="cp-grid-header">
                    <h2><CreditCard size={20} /> Hakedişler</h2>
                    <div className="cp-grid-tools">
                        <span className="cp-grid-count">{totalRecords} kayıt</span>
                        {embedded && (
                            <button className="btn btn-secondary btn-sm" onClick={fetchPayments}>
                                <RefreshCw size={14} />
                                Yenile
                            </button>
                        )}
                    </div>
                </div>
                <DataGrid
                    data={payments}
                    columns={columns}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={handlePageSizeChange}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    filters={filters}
                    filterTypes={filterTypes}
                    onFilterChange={handleFilterChange}
                    renderCard={renderCard}
                    emptyIcon={CreditCard}
                    emptyMessage={isCurrentWeek ? 'Bu hafta henüz kapanmadı.' : 'Bu haftaya ait kurye hakedişi yok.'}
                    emptyHint={
                        <ul>
                            {isCurrentWeek ? (
                                <li>
                                    Kurye hakedişleri <strong>Pazartesi 08:00</strong>'de, tamamlanmış hafta için
                                    üretilir. Yürüyen haftanın kaydı henüz açılmadı.
                                </li>
                            ) : (
                                <li>O hafta teslimatı ve mesaisi olmayan kurye için kayıt açılmaz.</li>
                            )}
                            <li>
                                Kayıt, kuryenin o hafta <strong>filonuza onaylı üye</strong> olmasına bağlıdır.
                            </li>
                            <li>
                                Aracılık modelinde çalışan filoların kuryelerine ücretini <strong>filo</strong> öder;
                                bu teslimatlar platform hakedişinde görünmez.
                            </li>
                        </ul>
                    }
                    loadingMessage="Yükleniyor..."
                />
            </div>

            {renderDetail()}
        </div>
    );
};

export default CourierPaymentsPage;
